/**
 * Inbox sub-tab — list of conversations on the left, thread + reply box on
 * the right (M1.14 wave 1).
 *
 * The thin reader/sender note from the legacy comment still applies:
 * outbound dispatch isn't wired yet, so replies persist as "not dispatched"
 * messages until channel connectors land.
 *
 * The four small pieces (FilterChip, ConversationRow, EmptyConversations,
 * MessageBubble) live in this file rather than their own — they're only
 * used here and splitting further would add noise without payoff.
 */
import type {
  CommunicationChannelView,
  CommunicationConversationSummary,
  CommunicationMessageView,
} from '@peerless/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox as InboxIcon, Loader2, Send, ShieldAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { ApiError, apiRequest } from '../../lib/api-client';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query-keys';

import { KIND_META, type IconLike } from './kind-meta';


export function InboxPane(): JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filterChannelId, setFilterChannelId] = useState<string | null>(null);

  const channelsQuery = useQuery({
    queryKey: queryKeys.communication.channels(),
    queryFn: () => apiRequest<CommunicationChannelView[]>('/communication/channels'),
  });
  const conversationsQuery = useQuery({
    queryKey: queryKeys.communication.conversations({ channelId: filterChannelId }),
    queryFn: () => {
      const qs = filterChannelId ? `?channelId=${filterChannelId}` : '';
      return apiRequest<CommunicationConversationSummary[]>(
        `/communication/conversations${qs}`,
      );
    },
  });

  const channels = channelsQuery.data ?? [];
  const conversations = conversationsQuery.data ?? [];

  return (
    <div className="grid flex-1 grid-cols-[280px_1fr] divide-x divide-border overflow-hidden">
      {/* Conversation list */}
      <aside className="flex min-h-0 min-w-0 flex-col overflow-hidden bg-surface">
        <div className="flex items-center gap-1.5 overflow-x-auto border-b border-border px-3 py-2">
          <FilterChip
            active={filterChannelId === null}
            onClick={() => setFilterChannelId(null)}
            label={`All${channels.length > 0 ? ` · ${channels.length}` : ''}`}
            Icon={InboxIcon}
          />
          {channels.map((ch) => {
            const meta = KIND_META[ch.kind];
            return (
              <FilterChip
                key={ch.id}
                active={filterChannelId === ch.id}
                onClick={() => setFilterChannelId(ch.id)}
                label={ch.label}
                Icon={meta.Icon}
              />
            );
          })}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {conversationsQuery.isLoading ? (
            <p className="px-2 py-3 text-xs text-muted">Loading…</p>
          ) : conversations.length === 0 ? (
            <EmptyConversations channelCount={channels.length} />
          ) : (
            <ul className="space-y-1">
              {conversations.map((c) => (
                <ConversationRow
                  key={c.id}
                  conversation={c}
                  selected={selectedId === c.id}
                  onClick={() => setSelectedId(c.id)}
                />
              ))}
            </ul>
          )}
        </div>
      </aside>

      {/* Thread */}
      <main className="flex min-h-0 min-w-0 flex-col overflow-hidden bg-bg">
        {selectedId ? (
          <ConversationThread conversationId={selectedId} />
        ) : (
          <div className="grid flex-1 place-items-center px-8 text-center">
            <div className="max-w-md space-y-3">
              <div className="mx-auto grid size-12 place-items-center rounded-full bg-accent/10 text-accent">
                <InboxIcon className="size-5" />
              </div>
              <h2 className="text-base font-semibold text-text">
                Pick a conversation
              </h2>
              <p className="text-sm text-muted">
                {channels.length === 0
                  ? 'No channels yet. An admin can add one under Settings → Communication.'
                  : 'Choose a conversation from the list to read messages and reply.'}
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  Icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  Icon: IconLike | LucideIcon;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-sm border px-2 py-1 text-[11px] transition',
        active
          ? 'border-accent bg-accent/10 text-text'
          : 'border-border bg-bg/30 text-muted hover:bg-bg hover:text-text',
      )}
    >
      <Icon className="size-3" />
      {label}
    </button>
  );
}

function ConversationRow({
  conversation: c,
  selected,
  onClick,
}: {
  conversation: CommunicationConversationSummary;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  const meta = KIND_META[c.channelKind];
  const time = c.lastMessageAt
    ? new Date(c.lastMessageAt).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full flex-col gap-1 rounded-sm border px-2.5 py-2 text-left transition',
          selected
            ? 'border-accent bg-accent/10'
            : 'border-transparent hover:bg-bg',
        )}
      >
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="flex items-center gap-1.5 text-muted">
            <meta.Icon className="size-3" />
            <span className="text-text">{c.customerName ?? c.customerHandle ?? '—'}</span>
          </span>
          <span className="text-[10px] text-muted">{time}</span>
        </div>
        <p className="line-clamp-2 text-[11px] text-muted">
          {c.lastMessagePreview ?? <em>No messages yet</em>}
        </p>
      </button>
    </li>
  );
}

function EmptyConversations({
  channelCount,
}: {
  channelCount: number;
}): JSX.Element {
  return (
    <div className="px-3 py-6 text-center">
      <p className="text-xs text-muted">
        {channelCount === 0
          ? 'No channels granted. Ask an admin for access.'
          : 'No conversations yet — inbound messages will appear here once channel connectors are wired.'}
      </p>
    </div>
  );
}

function ConversationThread({
  conversationId,
}: {
  conversationId: string;
}): JSX.Element {
  const qc = useQueryClient();
  const messagesQuery = useQuery({
    queryKey: queryKeys.communication.messages(conversationId),
    queryFn: () =>
      apiRequest<CommunicationMessageView[]>(
        `/communication/conversations/${conversationId}/messages`,
      ),
  });

  const [draft, setDraft] = useState('');
  const sendMutation = useMutation({
    mutationFn: (body: string) =>
      apiRequest<CommunicationMessageView>(
        `/communication/conversations/${conversationId}/messages`,
        { method: 'POST', body: { body } },
      ),
    onSuccess: () => {
      setDraft('');
      void qc.invalidateQueries({ queryKey: queryKeys.communication.messages(conversationId) });
      void qc.invalidateQueries({ queryKey: queryKeys.communication.all() });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Send failed'),
  });

  const messages = messagesQuery.data ?? [];

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {messagesQuery.isLoading ? (
          <p className="text-xs text-muted">Loading…</p>
        ) : messages.length === 0 ? (
          <p className="text-xs text-muted">No messages yet.</p>
        ) : (
          <ul className="space-y-3">
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}
          </ul>
        )}
      </div>
      <div className="border-t border-border bg-surface p-3">
        <div className="flex items-start gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder="Type a reply…"
            className="block flex-1 rounded-sm border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-muted focus:border-accent focus:outline-none"
          />
          <button
            type="button"
            onClick={() => draft.trim() && sendMutation.mutate(draft.trim())}
            disabled={!draft.trim() || sendMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-sm bg-accent px-3 py-2 text-sm font-medium text-accent-fg hover:opacity-90 disabled:opacity-50"
          >
            {sendMutation.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Send className="size-3.5" />
            )}
            Send
          </button>
        </div>
        <p className="mt-1.5 flex items-center gap-1 text-[11px] text-muted">
          <ShieldAlert className="size-3" />
          Replies are saved locally — outbound dispatch lands with the channel connectors.
        </p>
      </div>
    </div>
  );
}

function MessageBubble({
  message: m,
}: {
  message: CommunicationMessageView;
}): JSX.Element {
  const isMine = m.direction === 'outbound';
  return (
    <li className={cn('flex', isMine ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[70%] rounded-sm border px-3 py-2 text-sm',
          isMine
            ? 'border-accent/40 bg-accent/10 text-text'
            : 'border-border bg-surface text-text',
        )}
      >
        {m.authorName && (
          <p className="text-[10px] uppercase tracking-tight text-muted">
            {m.authorName}
          </p>
        )}
        <p className="whitespace-pre-wrap">{m.body ?? <em>(empty)</em>}</p>
        <p className="mt-1 text-[10px] text-muted">
          {new Date(m.createdAt).toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
          {m.errorMessage && (
            <span className="ml-1 text-warning"> · {m.errorMessage}</span>
          )}
        </p>
      </div>
    </li>
  );
}
